import { clamp } from '../core/utils.js';
import { DEFAULT_INSTRUMENT, normalizeInstrument } from '../core/instruments.js';
import { pianoSamples, createInstrumentVoice } from './instruments.js';
import { audioSession, resumeAudioContext } from './audio-session.js';

// A monotonic practice clock with a short Web Audio scheduling window. Notes
// are already expressed in the practice item's stable timeline.
export class MusicTransport {
  constructor({ onState = () => {}, onFrame = () => {}, onError = () => {}, now = () => performance.now() / 1000, audioFactory = () => new (globalThis.AudioContext || globalThis.webkitAudioContext)(), schedule = callback => setInterval(callback, 25), unschedule = id => clearInterval(id), samples = pianoSamples } = {}) {
    Object.assign(this, { onState, onFrame, onError, now, audioFactory, schedule, unschedule, samples });
    this.notes = []; this.duration = 0; this.position = 0; this.rate = 1; this.paused = true; this.muted = false;
    this.voices = new Set(); this.generation = 0; this.audioGeneration = 0; this.audioReady = false; this.resumeToken = 0;
    this.instrument = DEFAULT_INSTRUMENT; this.pendingPlay = false;
  }
  get time() { return clamp(this.position + (this.paused ? 0 : (this.now() - this.startedAt) * this.rate), 0, this.duration); }
  get state() { return { time: this.time, duration: this.duration, paused: this.paused && !this.pendingPlay, rate: this.rate, muted: this.muted, volume: 1, seeking: false }; }
  emit(event) { this.onState({ event, ...this.state }); }
  setNotes(notes, duration) {
    const wasPlaying = !this.paused || this.pendingPlay, position = this.time;
    this.pause(); this.notes = [...notes].sort((a,b) => a.start - b.start || a.pitch - b.pitch); this.duration = duration;
    this.position = clamp(position, 0, duration); this.resetSchedule();
    if (wasPlaying) this.play().catch(this.onError);
  }
  ensureAudio() {
    audioSession.sync();
    if (this.context && (this.context.state === 'interrupted' || this.audioStalled())) this.disposeAudio();
    if (!this.context || this.context.state === 'closed') {
      const context = this.audioFactory(); this.context = context;
      this.output = context.createGain(); this.output.gain.value = .6;
      this.compressor = context.createDynamicsCompressor();
      this.compressor.threshold.value = -12; this.compressor.knee.value = 12; this.compressor.ratio.value = 6;
      this.compressor.attack.value = .003; this.compressor.release.value = .15;
      this.output.connect(this.compressor); this.compressor.connect(context.destination);
      context.onstatechange = () => {
        if (context !== this.context) return;
        if ((!this.paused || this.pendingPlay || this.voices.size) && ['suspended','interrupted','closed'].includes(context.state)) {
          this.pause(); this.onError(new Error('Instrument audio was interrupted. Press Play to resume.'));
        }
      };
      this.checkAudioClock();
    }
    return this.context;
  }
  checkAudioClock() { this.audioClock = { time: this.context.currentTime, at: this.now() }; }
  audioStalled() {
    if (!this.context || this.context.state !== 'running') return false;
    if (!this.audioClock || this.context.currentTime !== this.audioClock.time) this.checkAudioClock();
    return this.now() - this.audioClock.at >= 1;
  }
  async resumeAudio(context) {
    const token = ++this.resumeToken;
    try { await resumeAudioContext(context); }
    catch (error) { if (context === this.context && token === this.resumeToken) this.disposeAudio(); throw error; }
    if (context === this.context && token === this.resumeToken) this.checkAudioClock();
  }
  disposeAudio() {
    const context = this.context;
    if (!context) return;
    context.onstatechange = null; this.silence();
    this.output?.disconnect(); this.compressor?.disconnect();
    this.context = null; this.output = null; this.compressor = null; this.audioClock = null; this.audioReady = false; ++this.resumeToken;
    if (context.state !== 'closed') context.close().catch(() => {});
  }
  async play() {
    if (!this.paused || this.pendingPlay) return;
    const generation = ++this.generation;
    this.pendingPlay = true; this.emit('waiting');
    try {
      if (!this.muted) {
        const context = this.ensureAudio();
        await Promise.all([this.resumeAudio(context), this.prepare(context, this.notes.map(note => note.pitch))]);
        if (generation !== this.generation || context !== this.context) return;
        if (context.state !== 'running') throw new Error('Tap Play to enable instrument sound in this browser.');
      }
      if (generation !== this.generation) return;
      this.audioReady = !this.muted;
      if (this.position >= this.duration) this.position = 0;
      this.startedAt = this.now(); this.paused = false; this.pendingPlay = false; this.resetSchedule();
      this.timer = this.schedule(() => this.tick()); this.emit('play'); this.tick();
    } catch (error) {
      if (generation === this.generation) throw error;
    } finally {
      if (generation === this.generation && this.pendingPlay) { this.pendingPlay = false; this.emit('pause'); }
    }
  }
  prepare(context, pitches) { return this.instrument === DEFAULT_INSTRUMENT ? this.samples.load(context, pitches) : Promise.resolve(); }
  setInstrument(id) {
    const instrument = normalizeInstrument(id);
    if (instrument === this.instrument) return Promise.resolve();
    const playing = !this.paused || this.pendingPlay;
    this.pause(); this.instrument = instrument;
    return playing ? this.play() : Promise.resolve();
  }
  preview(pitch, muted = false) { return this.previewNotes([pitch], muted); }
  async previewNotes(pitches, muted = false) {
    if (muted || !pitches.length || pitches.some(pitch => !Number.isInteger(pitch) || pitch < 0 || pitch > 127)) return;
    const generation = this.generation, context = this.ensureAudio();
    try { await Promise.all([this.resumeAudio(context), this.prepare(context, pitches)]); }
    catch (error) { if (generation === this.generation) throw error; else return; }
    if (generation !== this.generation || context !== this.context || context.state !== 'running') return;
    for (const pitch of pitches) this.sound({pitch, start:0, duration:.75, velocity:.8}, 0);
  }
  pause() {
    this.position = this.time; this.paused = true; this.pendingPlay = false; ++this.generation;
    this.audioReady = false; ++this.audioGeneration;
    if (this.timer !== undefined) this.unschedule(this.timer); this.timer = undefined;
    this.silence(); this.emit('pause');
  }
  seek(time) { if (!Number.isFinite(time)) return; this.position = clamp(time, 0, this.duration); this.startedAt = this.now(); this.silence(); this.resetSchedule(); this.emit('seeked'); if (!this.paused) this.tick(); }
  setRate(rate) { this.position = this.time; this.startedAt = this.now(); this.rate = clamp(rate, .25, 2); this.silence(); this.resetSchedule(); this.emit('ratechange'); }
  setMuted(muted) {
    const pendingPlay = this.pendingPlay;
    if (pendingPlay) this.pause();
    const generation = ++this.audioGeneration;
    this.audioReady = false;
    this.muted = !!muted; this.silence(); this.resetSchedule(); this.emit('volumechange');
    if (pendingPlay) { this.play().catch(this.onError); return; }
    if (!this.paused && !muted) {
      try {
        const context = this.ensureAudio();
        Promise.all([this.resumeAudio(context), this.prepare(context, this.notes.map(note => note.pitch))]).then(() => {
          if (generation !== this.audioGeneration || context !== this.context || this.paused || this.muted) return;
          if (context.state !== 'running') throw new Error('Tap Play to enable instrument sound.');
          this.audioReady = true; this.resetSchedule(); this.tick();
        }).catch(error => {
          if (generation === this.audioGeneration && !this.muted) { this.pause(); this.onError(error); }
        });
      }
      catch (error) { this.pause(); this.onError(error); }
    }
  }
  resetSchedule() {
    const time = this.time; let lo = 0, hi = this.notes.length;
    while (lo < hi) { const mid = (lo + hi) >> 1; if (this.notes[mid].start < time) lo = mid + 1; else hi = mid; }
    this.cursor = lo; this.resumeHeld = true;
  }
  tick() {
    if (this.paused) return;
    const time = this.time;
    if (time >= this.duration) { this.pause(); this.position = this.duration; this.emit('ended'); return; }
    if (!this.muted && this.audioReady && this.context?.state === 'running') {
      if (this.audioStalled()) {
        this.pause(); this.disposeAudio(); this.onError(new Error('Instrument audio stopped responding. Press Play to reconnect sound.')); return;
      }
      if (this.resumeHeld) {
        this.resumeHeld = false;
        for (let index = 0; index < this.cursor; index++) { const note = this.notes[index]; if (note.start + note.duration > time) this.sound(note, time); }
      }
      const horizon = time + .12 * this.rate;
      while (this.cursor < this.notes.length && this.notes[this.cursor].start <= horizon) {
        const note = this.notes[this.cursor++]; if (note.start + note.duration > time) this.sound(note, time);
      }
    }
    this.onFrame(this.state); this.emit('timeupdate');
  }
  sound(note, time) {
    if (this.voices.size >= 64 || note.velocity === 0) return;
    const context = this.context, age = Math.max(0, time - note.start), start = context.currentTime + Math.max(0, note.start - time) / this.rate;
    const length = Math.max(.005, (note.duration - age) / this.rate);
    const voice = createInstrumentVoice(context, this.output, this.instrument, note, { start, length, age: age / this.rate }, this.samples, () => this.voices.delete(voice));
    if (voice) this.voices.add(voice);
  }
  silence() {
    for (const voice of this.voices) voice.stop();
    this.voices.clear();
  }
  destroy() { this.pause(); this.disposeAudio(); }
}
